import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { PopoverModal } from 'sutra/components/PopoverModal/PopoverModal'

import { CheckboxListOption } from 'features/charts-v2/components/CheckboxListOption'
import { RadioListOption } from 'features/charts-v2/components/RadioListOption'
import {
  DATA_TYPE_LABELS,
  PROJECT_DATA_TYPES,
  ProjectUsage,
  USAGE_LABELS,
} from 'features/projects/constants'
import { BulkUpdatePayload } from 'features/projects/service/service'
import { Project } from 'features/projects/types'
import { PressableToggleButtonGroup } from 'src/components/ui/PressableToggleButton'

type Patch = BulkUpdatePayload['body']

type FieldKey = 'usage' | 'data_types' | 'is_internal' | 'archived'

const USAGE_GROUPS: Array<{ label: string; values: ProjectUsage[] }> = [
  { label: 'Live Customers', values: ['client', 'trial', 'pilot'] },
  { label: 'Sales', values: ['gold_star', 'demo', 'sample'] },
  { label: 'Customer Success', values: ['training'] },
  { label: 'Internal', values: ['testing', 'copy'] },
]

const USAGE_SUBTITLES: Partial<Record<ProjectUsage, string>> = {
  copy: 'Used to backup data from live hubs',
}

type FieldValues = {
  usage: string | null
  data_types: string[]
  is_internal: boolean
  archived: boolean
}

function seedValues(projects: Project[]): FieldValues {
  if (projects.length === 1) {
    const p = projects[0]!
    return {
      usage: p.usage ?? null,
      data_types: p.data_types ?? [],
      is_internal: p.is_internal ?? false,
      archived: p.archived_at != null,
    }
  }
  return {
    usage: null,
    data_types: [],
    is_internal: false,
    archived: false,
  }
}

function buildPatch(field: FieldKey, v: FieldValues): Patch {
  switch (field) {
    case 'usage':
      return { usage: v.usage ?? null }
    case 'data_types':
      return { data_types: v.data_types.length > 0 ? v.data_types : null }
    case 'is_internal':
      return { is_internal: v.is_internal }
    case 'archived':
      return { archived: v.archived }
  }
}

function isFieldDirty(
  field: FieldKey,
  values: FieldValues,
  seed: FieldValues,
): boolean {
  switch (field) {
    case 'usage':
      return values.usage !== seed.usage
    case 'data_types':
      return (
        values.data_types.length !== seed.data_types.length ||
        values.data_types.some((v, i) => v !== seed.data_types[i])
      )
    case 'is_internal':
      return values.is_internal !== seed.is_internal
    case 'archived':
      return values.archived !== seed.archived
  }
}

const FIELD_OPTIONS: { label: string; value: FieldKey }[] = [
  { label: 'Usage', value: 'usage' },
  { label: 'Data Types', value: 'data_types' },
  { label: 'Internal', value: 'is_internal' },
  { label: 'Archived', value: 'archived' },
]

export type EditProjectsModalProps = {
  open: boolean
  projects: Project[]
  loading?: boolean
  onClose: () => void
  onApply: (patch: Patch) => Promise<void>
}

export const EditProjectsModal: React.FC<EditProjectsModalProps> = ({
  open,
  projects,
  loading = false,
  onClose,
  onApply,
}) => {
  const title =
    projects.length === 1
      ? `Edit "${projects[0]!.title}"`
      : `Bulk edit (${projects.length} hubs)`

  const seed = useMemo(() => seedValues(projects), [projects])

  const [selectedField, setSelectedField] = useState<FieldKey>('usage')
  const [values, setValues] = useState<FieldValues>(() => seedValues(projects))

  useEffect(() => {
    setSelectedField('usage')
    setValues(seedValues(projects))
  }, [projects])

  const isDirty = isFieldDirty(selectedField, values, seed)
  const patch = useMemo(
    () => buildPatch(selectedField, values),
    [selectedField, values],
  )

  const handleApply = async (): Promise<void> => {
    await onApply(patch)
  }

  const handleClear = (): void => {
    setValues(seed)
  }

  const handleFieldChange = (next: FieldKey): void => {
    setSelectedField(next)
    setValues(seed)
  }

  return (
    <PopoverModal
      isOpen={open}
      title={title}
      onClose={onClose}
      width={480}
      footer={
        <FooterRow>
          <ClearButton onClick={handleClear} disabled={!isDirty || loading}>
            Clear
          </ClearButton>
          <ApplyButton onClick={handleApply} disabled={!isDirty || loading}>
            {loading
              ? 'Applying…'
              : isDirty
                ? `Apply (${projects.length})`
                : 'Apply'}
          </ApplyButton>
        </FooterRow>
      }
      content={
        <ModalBody>
          <SegmentedWrapper>
            <FieldEditLabel>Field to edit:</FieldEditLabel>
            <PressableToggleButtonGroup<FieldKey>
              block
              value={selectedField}
              onChange={handleFieldChange}
              options={FIELD_OPTIONS}
            />
          </SegmentedWrapper>

          {selectedField === 'usage' && (
            <FieldSection>
              {USAGE_GROUPS.map((group) => (
                <UsageGroup key={group.label}>
                  <UsageGroupLabel>{group.label}</UsageGroupLabel>
                  {group.values.map((v) => (
                    <RadioListOption
                      key={v}
                      label={USAGE_LABELS[v]}
                      subtitle={USAGE_SUBTITLES[v]}
                      isSelected={values.usage === v}
                      onClick={() => {
                        setValues((prev) => ({ ...prev, usage: v }))
                      }}
                    />
                  ))}
                </UsageGroup>
              ))}
              <UsageGroup>
                <UsageGroupLabel>Other</UsageGroupLabel>
                <RadioListOption
                  label="None"
                  subtitle='Usage is "None" by default'
                  isSelected={values.usage === null}
                  onClick={() => {
                    setValues((prev) => ({ ...prev, usage: null }))
                  }}
                />
              </UsageGroup>
            </FieldSection>
          )}

          {selectedField === 'data_types' && (
            <FieldSection>
              {PROJECT_DATA_TYPES.map((dt) => {
                const isSelected = values.data_types.includes(dt)
                return (
                  <CheckboxListOption
                    key={dt}
                    label={DATA_TYPE_LABELS[dt]}
                    isSelected={isSelected}
                    onClick={() => {
                      setValues((prev) => ({
                        ...prev,
                        data_types: isSelected
                          ? prev.data_types.filter((x) => x !== dt)
                          : [...prev.data_types, dt],
                      }))
                    }}
                  />
                )
              })}
            </FieldSection>
          )}

          {selectedField === 'is_internal' && (
            <FieldSection>
              <RadioListOption
                label="External"
                subtitle="Client and prospect facing"
                isSelected={!values.is_internal}
                onClick={() => {
                  setValues((prev) => ({ ...prev, is_internal: false }))
                }}
              />
              <RadioListOption
                label="Internal"
                subtitle="For internal purposes only"
                isSelected={values.is_internal}
                onClick={() => {
                  setValues((prev) => ({ ...prev, is_internal: true }))
                }}
              />
              <Caption>
                Hubs are external by default, until manually marked as
                &quot;internal&quot;
              </Caption>
            </FieldSection>
          )}

          {selectedField === 'archived' && (
            <FieldSection>
              <RadioListOption
                label="Active"
                subtitle="All projects are active by default."
                isSelected={!values.archived}
                onClick={() => {
                  setValues((prev) => ({ ...prev, archived: false }))
                }}
              />
              <RadioListOption
                label="Archive"
                subtitle="The project will not be deleted. No data will be lost. This can be reversed at any time."
                isSelected={values.archived}
                onClick={() => {
                  setValues((prev) => ({ ...prev, archived: true }))
                }}
              />
            </FieldSection>
          )}
        </ModalBody>
      }
    />
  )
}

const SegmentedWrapper = styled.div`
  margin-bottom: 20px;
`

const FieldEditLabel = styled.span`
  display: block;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);
  margin: 8px 0;
`

const FieldSection = styled.div`
  margin-bottom: 16px;
`

const Caption = styled.div`
  margin-top: 10px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
`

const UsageGroup = styled.div`
  margin-top: 10px;
`

const UsageGroupLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: rgba(0, 0, 0, 0.45);
  margin-bottom: 4px;
  padding: 0 4px;
`

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 400px;
`

const FooterRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`

const ClearButton = styled.button<{ disabled?: boolean }>`
  padding: 5px 16px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  background: white;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  font-size: 14px;

  &:hover:not(:disabled) {
    color: #40a9ff;
    border-color: #40a9ff;
  }
`

const ApplyButton = styled.button<{ disabled?: boolean }>`
  padding: 5px 16px;
  border: none;
  border-radius: 6px;
  background: ${({ disabled }) => (disabled ? '#d9d9d9' : '#1677ff')};
  color: ${({ disabled }) => (disabled ? 'rgba(0,0,0,0.25)' : 'white')};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  font-size: 14px;

  &:hover:not(:disabled) {
    background: #4096ff;
  }
`
